import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";
import { useConfirm } from "../../ui/components/ConfirmDialog";

type ProvisionedRow = {
  deviceId: string;
  shortId?: string;
  pairingNonce: string;
  claimUrl: string;
  pairingNonceExpiresAt: string;
};

type FailureRow = {
  deviceId: string;
  error: string;
};

function randomSuffix(len = 8): string {
  const alphabet = "abcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < len; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

function buildAutoDeviceIds(count: number, prefixTrimmed: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  while (out.length < count) {
    const suf = randomSuffix(8);
    const id = prefixTrimmed ? `${prefixTrimmed}-${suf}` : suf;
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

function extractErrorMessage(e: unknown): string {
  if (e && typeof e === "object") {
    const ax = e as { response?: { data?: { error?: string } }; message?: string };
    const fromBody = ax.response?.data?.error;
    if (typeof fromBody === "string" && fromBody) return fromBody;
    if (typeof ax.message === "string") return ax.message;
  }
  return "request failed";
}

function csvEscapeCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function toProvisionCsv(rows: ProvisionedRow[]): string {
  const header = "deviceId,shortId,claimUrl,pairingNonce,pairingNonceExpiresAt";
  const lines = rows.map((r) =>
    [
      csvEscapeCell(r.deviceId),
      csvEscapeCell(r.shortId ?? ""),
      csvEscapeCell(r.claimUrl),
      csvEscapeCell(r.pairingNonce),
      csvEscapeCell(r.pairingNonceExpiresAt),
    ].join(",")
  );
  return [header, ...lines].join("\n");
}

const Wrap = styled.div`
  min-height: calc(100vh - 64px);
  background: #f0f0f0;
  padding: 1.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
  @media (max-width: 700px) {
    padding: 12px 8px;
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #232323;
  box-shadow: 6px 6px 0 #232323;
  padding: 1.25rem;
  @media (max-width: 700px) {
    padding: 1rem 0.85rem;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.35rem;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #444;
`;

const BackLink = styled(Link)`
  align-self: flex-start;
  font-size: 0.85rem;
  font-weight: 700;
  color: #232323;
  text-decoration: underline;
  &:hover {
    color: #000;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 800;
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TextInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 0.45rem 0.5rem;
  border: 1px solid #232323;
  font-family: inherit;
  font-size: 0.85rem;
  box-shadow: 3px 3px 0 #232323;
  box-sizing: border-box;
  max-width: 100%;
  @media (max-width: 700px) {
    min-width: 0;
    width: 100%;
  }
`;

const NumInput = styled(TextInput)`
  max-width: 120px;
  min-width: 100px;
  @media (max-width: 700px) {
    max-width: 100%;
    width: 100%;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  margin-top: 0.5rem;
  padding: 0.45rem 0.5rem;
  border: 1px solid #232323;
  font-family: inherit;
  font-size: 0.85rem;
  box-shadow: 3px 3px 0 #232323;
  resize: vertical;
  box-sizing: border-box;
  max-width: 100%;
`;

const PrimaryBtn = styled.button`
  padding: 0.45rem 0.85rem;
  border: 1px solid #232323;
  background: #ffd24a;
  color: #232323;
  font-family: inherit;
  font-weight: 800;
  font-size: 0.8rem;
  cursor: pointer;
  box-shadow: 3px 3px 0 #232323;
  &:hover:not(:disabled) {
    background: #ffdf73;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  @media (max-width: 700px) {
    width: 100%;
  }
`;

const SecondaryBtn = styled(PrimaryBtn)`
  background: #fff;
  &:hover:not(:disabled) {
    background: #e8e8e8;
  }
`;

const Explainer = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.72rem;
  color: #555;
  line-height: 1.4;
`;

const FlashOk = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: #15803d;
  font-weight: 700;
`;

const InlineErr = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: crimson;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.65rem;
  font-size: 0.85rem;
  cursor: pointer;
  user-select: none;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StickerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
  margin-top: 0.25rem;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const StickerCard = styled.div`
  background: #fff;
  border: 1px solid #000;
  width: 220px;
  min-height: 340px;
  padding: 0.75rem 0.65rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
  @media (max-width: 700px) {
    width: 100%;
    max-width: 100%;
  }
  @media screen {
    box-shadow: 6px 6px 0 #232323;
  }
  @media print {
    width: 58mm;
    min-height: 90mm;
    box-shadow: none !important;
  }
`;

const StickerHead = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #232323;
  padding-bottom: 0.35rem;
  margin-bottom: 0.5rem;
`;

const StickerHeadSub = styled.span`
  font-weight: 700;
  text-transform: lowercase;
  letter-spacing: 0;
`;

const QrWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.35rem 0 0.65rem;
  max-width: 100%;
  & svg {
    width: 140px !important;
    height: 140px !important;
    max-width: 100%;
  }
  @media (max-width: 700px) {
    & svg {
      width: min(150px, 100%) !important;
      height: auto !important;
    }
  }
  @media print {
    & svg {
      width: 150px !important;
      height: 150px !important;
    }
  }
`;

const ShortIdBig = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 0.06em;
`;

const DeviceIdSmall = styled.div`
  font-size: 0.65rem;
  color: #666;
  text-align: center;
  margin-top: 0.2rem;
  word-break: break-all;
`;

const StickerFoot = styled.div`
  margin-top: auto;
  padding-top: 0.65rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 0.35rem;
  font-size: 0.6rem;
  color: #444;
`;

const FailDetails = styled.details`
  margin-top: 0.65rem;
  font-size: 0.72rem;
  color: #232323;
  summary {
    cursor: pointer;
    font-weight: 700;
  }
`;

const FailList = styled.ul`
  margin: 0.35rem 0 0;
  padding-left: 1.1rem;
`;

const FieldStack = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FieldStackGrow = styled(FieldStack)`
  flex: 1;
  min-width: 160px;
  @media (max-width: 700px) {
    min-width: 0;
    width: 100%;
  }
`;

const MicroLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: #555;
`;

type PostDeviceResponse = {
  success?: boolean;
  device?: { deviceId?: string; shortId?: string };
  pairingNonce?: string;
  claimUrl?: string;
  pairingNonceExpiresAt?: string;
};

export default function AotdIrlAdminProvision() {
  const auth = useAppSelector((s) => s.auth);
  const confirm = useConfirm();
  const [count, setCount] = useState(5);
  const [prefix, setPrefix] = useState("");
  const [autoIds, setAutoIds] = useState(true);
  const [manualIds, setManualIds] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [provisioned, setProvisioned] = useState<ProvisionedRow[]>([]);
  const [failures, setFailures] = useState<FailureRow[]>([]);
  const [batchFlash, setBatchFlash] = useState<{ ok: number; fail: number } | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBatchFlash(null);
    setFailures([]);
    setFormErr(null);

    let deviceIds: string[] = [];
    if (autoIds) {
      const n = Math.min(50, Math.max(1, Math.floor(Number(count)) || 1));
      deviceIds = buildAutoDeviceIds(n, prefix.trim());
    } else {
      deviceIds = manualIds
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
        .slice(0, 50);
      if (deviceIds.length === 0) {
        setFormErr("enter at least one device id");
        return;
      }
    }

    setSubmitting(true);
    const nextProvisioned: ProvisionedRow[] = [];
    const nextFailures: FailureRow[] = [];

    for (const deviceId of deviceIds) {
      try {
        const resp = await apiClient.post<PostDeviceResponse>("/api/aotd-irl/admin/devices", {
          deviceId,
          preIssueNonce: true,
        });
        const d = resp.data;
        const nonce = d?.pairingNonce;
        const claimUrl = d?.claimUrl;
        const exp = d?.pairingNonceExpiresAt;
        if (typeof nonce === "string" && typeof claimUrl === "string" && typeof exp === "string") {
          const dev = d.device;
          nextProvisioned.push({
            deviceId: dev?.deviceId ?? deviceId,
            shortId: typeof dev?.shortId === "string" ? dev.shortId : undefined,
            pairingNonce: nonce,
            claimUrl,
            pairingNonceExpiresAt: exp,
          });
        } else {
          nextFailures.push({ deviceId, error: "missing nonce or claim url in response" });
        }
      } catch (err: unknown) {
        nextFailures.push({ deviceId, error: extractErrorMessage(err) });
      }
    }

    setProvisioned((prev) => [...nextProvisioned, ...prev]);
    setFailures(nextFailures);
    setBatchFlash({ ok: nextProvisioned.length, fail: nextFailures.length });
    setSubmitting(false);
  };

  const handleExportCsv = () => {
    if (provisioned.length === 0) return;
    const blob = new Blob([toProvisionCsv(provisioned)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aotd-irl-provision-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClear = async () => {
    const ok = await confirm({
      title: "Clear all stickers from this session?",
      message: "The provisioned list and failure log will be emptied. Devices already created aren't affected.",
      confirmLabel: "Clear",
      tone: "danger",
    });
    if (!ok) return;
    setProvisioned([]);
    setFailures([]);
    setBatchFlash(null);
  };

  const handlePrint = () => {
    window.print();
  };

  if (auth.bootstrapStatus !== "ready") {
    return null;
  }
  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }
  if (!checkRole(auth.role ?? "", "admin")) {
    return <Navigate to="/" replace />;
  }

  return (
    <Wrap>
      <style>{`
@media print {
  @page { size: A4; margin: 10mm; }
  body * { visibility: hidden; }
  .printable, .printable * { visibility: visible; }
  .printable { position: absolute; left: 0; top: 0; width: 100%; }
  .no-print { display: none !important; }
  .sticker-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6mm; }
  .sticker { page-break-inside: avoid; box-shadow: none !important; }
}
      `}</style>
      <Inner>
        <div className="no-print">
          <Card>
            <HeaderRow>
              <BackLink to="/aotd-irl/admin">← AOTD-IRL admin</BackLink>
              <Title>AOTD-IRL provisioning</Title>
              <Subtitle>bulk-create frames and print claim stickers</Subtitle>
            </HeaderRow>
          </Card>
        </div>

        <div className="no-print">
          <Card>
            <SectionTitle>Generate frames</SectionTitle>
            <form onSubmit={handleGenerate}>
              <FormRow>
                <FieldStack>
                  <MicroLabel>Number to generate</MicroLabel>
                  <NumInput
                    type="number"
                    min={1}
                    max={50}
                    value={count}
                    disabled={!autoIds}
                    onChange={(ev) => setCount(Number(ev.target.value))}
                    aria-label="number to generate"
                  />
                </FieldStack>
                <FieldStackGrow>
                  <MicroLabel>Prefix</MicroLabel>
                  <TextInput
                    value={prefix}
                    onChange={(ev) => setPrefix(ev.target.value)}
                    placeholder="optional"
                    disabled={!autoIds}
                    aria-label="device id prefix"
                    autoComplete="off"
                  />
                </FieldStackGrow>
                <PrimaryBtn type="submit" disabled={submitting}>
                  {submitting ? "generating…" : "Generate frames"}
                </PrimaryBtn>
              </FormRow>
              <CheckRow>
                <input
                  type="checkbox"
                  checked={autoIds}
                  onChange={(ev) => setAutoIds(ev.target.checked)}
                />
                <span>Auto-generate IDs</span>
              </CheckRow>
              {!autoIds && (
                <TextArea
                  value={manualIds}
                  onChange={(ev) => setManualIds(ev.target.value)}
                  placeholder="one deviceId per line (max 50)"
                  aria-label="manual device ids"
                  spellCheck={false}
                />
              )}
            </form>
            <Explainer>
              posts each id to the admin devices api with a pre-issued pairing nonce. failures are collected; successes
              appear as printable stickers below.
            </Explainer>
            {formErr && <InlineErr>{formErr}</InlineErr>}
            {batchFlash && (
              <FlashOk>
                created {batchFlash.ok} · failed {batchFlash.fail}
              </FlashOk>
            )}
            {failures.length > 0 && (
              <FailDetails>
                <summary>failed devices ({failures.length})</summary>
                <FailList>
                  {failures.map((f, i) => (
                    <li key={`${f.deviceId}-${i}`}>
                      <strong>{f.deviceId}</strong>: {f.error}
                    </li>
                  ))}
                </FailList>
              </FailDetails>
            )}
          </Card>
        </div>

        {provisioned.length > 0 && (
          <>
            <div className="no-print">
              <Card>
                <SectionTitle>Sticker sheet</SectionTitle>
                <Toolbar>
                  <PrimaryBtn type="button" onClick={handlePrint}>
                    Print sheet
                  </PrimaryBtn>
                  <SecondaryBtn type="button" onClick={handleExportCsv}>
                    Export CSV
                  </SecondaryBtn>
                  <SecondaryBtn type="button" onClick={handleClear}>
                    Clear
                  </SecondaryBtn>
                </Toolbar>
              </Card>
            </div>
            <div className="printable">
              <StickerGrid className="sticker-grid">
                {provisioned.map((row) => (
                  <StickerCard key={`${row.deviceId}-${row.pairingNonce}`} className="sticker">
                    <StickerHead>
                      AOTD frame <StickerHeadSub> · claim</StickerHeadSub>
                    </StickerHead>
                    <QrWrap className="sticker-qr">
                      <QRCodeSVG value={row.claimUrl} size={150} level="M" />
                    </QrWrap>
                    <ShortIdBig>{(row.shortId ?? "—").toString().toUpperCase()}</ShortIdBig>
                    <DeviceIdSmall>{row.deviceId}</DeviceIdSmall>
                    <StickerFoot>
                      <span>scan to claim</span>
                      <span>n450s.com/aotd</span>
                    </StickerFoot>
                  </StickerCard>
                ))}
              </StickerGrid>
            </div>
          </>
        )}
      </Inner>
    </Wrap>
  );
}
